import mixins from 'vue-typed-mixins';
import _ from 'lodash';
import dayjs from 'dayjs';

import { E取込データ種別 } from '@/openapi/enum';
import {
  取込Api,
  取込FileListItemResponse,
  取込Pdfカテゴリ試算表ListItemRequest,
  取込Pdf試算表ListItemRequest,
  帳票DynamicConfig,
  帳票ListItemRequest,
} from '@/openapi/user';

import Pdf試算表TabComponent from '@/components/取込/データ確認/pdf/試算表/Pdf試算表Tab.vue';
import C取込Error from '@/components/取込/error/取込Error.vue';
import { Pdf試算表Row, Pdf試算表Tab } from '@/components/取込/データ確認/pdf/試算表/Pdf試算表Tab';

import 取込Mixin, { Default分類 } from '@/entries/user/mixins/取込Mixin';
import UserApiUtility from '@/classes/common/UserApiUtility';
import { MONTHS_OF_YEAR } from '@/classes/common/Constants';

export default mixins(取込Mixin).extend({
  name: 'Pdf試算表Dialog',

  components: {
    Pdf試算表TabComponent,
    C取込Error,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data(): {
    pdf試算表Tabs: Pdf試算表Tab[];
  } {
    return {
      pdf試算表Tabs: [],
    };
  },

  computed: {
    tabs(): string[] {
      return this.pdf試算表Tabs.map((表) => this.get取込データ種別Label(表.取込データ種別));
    },
    pdf試算表Tab(): Pdf試算表Tab | undefined {
      return this.pdf試算表Tabs[this.tabIndex()];
    },
  },

  watch: {
    async visible(): Promise<void> {
      if (!this.visible) return;

      // 初期化
      this.setTabIndex(0, 0);
      this.pdf試算表Tabs = [];
      this.取込データ種別Errors = [];

      // 勘定科目、取込データ取得
      const isError = await this.load取込データs('試算表');

      // エラーがある場合はダイアログを閉じてreturn
      if (isError) {
        this.close();
        return;
      }

      // 取込データ設定
      this.pdf試算表Tabs = this.取込データs.map((d) => {
        const 取込データ種別 = d.取込データ種別!;
        const default分類 = this.getDefault分類(取込データ種別);
        const list = d.list!.map((item) => this.$_createPdf試算表Row(item, 取込データ種別, default分類));

        return {
          取込データ種別,
          selectedYms: list.length > 0 ? new Array(list[0].strValues.length) : [],
          list,
        };
      });
    },
  },

  methods: {
    close(): void {
      this.$emit('update:visible', false);
    },
    async save(): Promise<void> {
      this.取込データ種別Errors = [];

      // 取込データが1件もない場合はアラートを表示してreturn
      if (this.pdf試算表Tabs.every((tab) => tab.list.length === 0)) {
        alert('取込対象がありません');
        return;
      }

      // 月の選択列での絞り込み関数
      const isValidMonth = (value: number | undefined): boolean => value !== undefined && value > 0;
      const getFilteredTab = (tab: Pdf試算表Tab): Pdf試算表Tab => ({
        取込データ種別: tab.取込データ種別,
        selectedYms: tab.selectedYms.filter((ym) => isValidMonth(ym)),
        list: tab.list.map((row) => ({
          ...row,
          strValues: row.strValues.filter((_, i) => isValidMonth(tab.selectedYms[i])),
        })),
      });

      // 入力チェック
      this.pdf試算表Tabs.forEach((tab) => {
        // 取込データがない場合は次へ
        if (tab.list.length === 0) return;

        // 月の選択列で絞り込み
        const filteredTab = getFilteredTab(tab);

        // 月の選択チェック
        const uniqueYms = [...new Set(filteredTab.selectedYms)];
        if (uniqueYms.length < 12) {
          this.add取込Error(tab.取込データ種別, '選択肢', '1～12月まで選択してください');
        }
        if (filteredTab.selectedYms.length !== uniqueYms.length) {
          this.add取込Error(tab.取込データ種別, '選択肢', '同じ月が選択されています');
        }

        // 入力項目チェック
        filteredTab.list.forEach((row, i) => {
          // 勘定科目の存在チェック
          if (!row.isInMaster) {
            this.add取込Error(tab.取込データ種別, '勘定科目', 'マスタに存在しません', i);
          }

          // 数値チェック
          row.strValues.forEach((strVal, j) => {
            if (!this.$_validateValue(strVal)) {
              this.add取込Error(tab.取込データ種別, `${filteredTab.selectedYms[j]}月`, '数字を入力してください', i);
            }
          });
        });
      });

      // 入力チェック不可の場合はここでreturn
      if (this.取込データ種別Errors.length > 0) return;

      // 送信データ成形
      const requests: 取込Pdfカテゴリ試算表ListItemRequest[] = this.pdf試算表Tabs.map(
        (tab): 取込Pdfカテゴリ試算表ListItemRequest => {
          // 月の選択列で絞り込み
          const filteredTab = getFilteredTab(tab);

          const month = this.計画書.c計画0期の決算期 % Math.floor(this.計画書.c計画0期の決算期 / 100);
          const dayjsMonth = dayjs().month(month);
          // 月順にソートした選択月のインデックス生成
          // ※<c計画0期の決算期 の 11か月前の月> ～ <c計画0期の決算期 の月> の順番となるよう取得
          const ymSortIndexes: number[] = [];
          for (let i = 0; i < MONTHS_OF_YEAR; i++) {
            const m = dayjsMonth.add(i, 'month').month() + 1;
            const idx = filteredTab.selectedYms.findIndex((ym) => ym === m);
            ymSortIndexes.push(idx);
          }

          // リスト生成
          const list = filteredTab.list.map((row) => {
            // 入力値を月順にソート、数値に変換して格納
            row.request.monthlyValues = ymSortIndexes.map((idx) => this.$_convertInputToValue(row.strValues[idx]));
            return row.request;
          });

          return {
            cカテゴリ: this.getCカテゴリFrom取込データ種別(tab.取込データ種別),
            list,
          };
        }
      );

      requests.forEach((request) => {
        const list = request.list!;
        const unique科目名 = [...new Set(list.map((row) => row.取込勘定科目!.c勘定科目))];
        request.list = unique科目名.map((科目名) => {
          const filtered = list.filter((row) => row.取込勘定科目!.c勘定科目 === 科目名);
          const first = filtered[0];

          return {
            勘定科目Pk: first.勘定科目Pk,
            取込勘定科目: first.取込勘定科目,
            // 決算書と試算表で以下の部分しか差異がないので
            // 取込Pdf決算書ListItemRequest | 取込Pdf試算表ListItemRequest
            // とかで処理を纏めようとしたが、何とも上手くいかなかった
            monthlyValues: [...first.monthlyValues.keys()].map((index) =>
              _.sum(filtered.map((row) => row.monthlyValues[index]))
            ),
          };
        });
      });

      // 送信
      await UserApiUtility.ogApi(取込Api).save取込Pdf試算表(
        this.計画書.pk,
        this.importInfo.取込分類,
        requests,
        this.importInfo.部門pk
      );

      this.$emit('saved');
      this.close();
    },
    output試算表(): void {
      const createRequest = (tab: Pdf試算表Tab): 帳票ListItemRequest => {
        const templateRows = [
          this.outputConfig.template.header,
          ...new Array(tab.list.length).fill(this.outputConfig.template.row),
        ];
        const values = [
          ['', ...tab.selectedYms.map((ym) => `${ym}月`)],
          ...tab.list.map((row) => [row.request.取込勘定科目?.c勘定科目, ...row.strValues]),
        ];
        const dynamicConfigs: 帳票DynamicConfig[] = [
          {
            columns: [
              this.outputConfig.column.勘定科目,
              ...new Array(tab.selectedYms.length).fill(this.outputConfig.column.value),
            ],
          },
        ];
        return {
          templateName: '取込試算表',
          sheetName: this.get取込データ種別Label(tab.取込データ種別),
          templateRows,
          values,
          dynamicConfigs,
        };
      };

      this.outputXlsx(this.pdf試算表Tabs, createRequest);
    },
    $_createPdf試算表Row(
      item: 取込FileListItemResponse,
      取込データ種別: E取込データ種別,
      default分類: Default分類
    ): Pdf試算表Row {
      const request: 取込Pdf試算表ListItemRequest = { monthlyValues: [] };
      const 勘定科目 = this.filter勘定科目(取込データ種別).find((勘) => 勘.c勘定科目 === item.勘定科目);
      this.set取込勘定科目(request, 勘定科目, item, default分類);

      return {
        selected勘定科目: null,
        strValues: item.values,
        isInMaster: 勘定科目 !== undefined,
        request,
      };
    },
    $_validateValue(input: string): boolean {
      return !isNaN(this.$_convertInputToValue(input));
    },
    $_convertInputToValue(input: string): number {
      return Number(input.replaceAll(',', ''));
    },
  },
});
